// view-truncate-ok-file: thanh Gantt rộng theo thời lượng, nhãn trong thanh phải một dòng (rê chuột xem đủ)
import React, { useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { useQuery } from "@tanstack/react-query";
import {
  format,
  differenceInDays,
  differenceInHours,
  addDays,
  addHours,
  startOfDay,
  parseISO,
  isSameDay,
  isToday,
} from "date-fns";
import { vi } from "date-fns/locale";
import { formatNumber, cn } from "@/lib/utils";
import {
  AlertTriangle,
  Clock,
  Calendar,
  Layers,
  Sparkles,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Cpu,
  CheckCircle2,
  Zap,
  Package,
  Wrench,
  Printer,
  Scissors,
  Lock,
  CalendarClock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { evaluateInkCuringDelay, describeOverlap, formatVnDateTime } from "@apv-erp/status";
import { ResequenceByDeadlineDialog } from "./ResequenceByDeadlineDialog";

export interface ProcessGanttViewProps {
  items: any[];
  isLoading?: boolean;
  dateParams: { from?: string; to?: string };
  highlightOrder?: string;
  onNavigateToOrder?: (poCode: string) => void;
  onNavigateToSalesOrder?: (salesOrderId: string) => void;
  onOpenMachineModal?: (step: any) => void;
  onOpenLogForm?: (step: any) => void;
  onRefetch?: () => void;
}

export interface CollisionInfo {
  machineId: string;
  stepA: any;
  stepB: any;
  overlapStart: number;
  overlapEnd: number;
}

export function getStepColorClasses(processNameOrType: string = "", isCollision: boolean = false) {
  if (isCollision) {
    return {
      bg: "bg-rose-50 border-rose-400 text-rose-900 hover:bg-rose-100",
      indicator: "bg-rose-500",
      badge: "bg-rose-100 text-rose-800 border-rose-300",
    };
  }

  const raw = processNameOrType.toLowerCase();
  if (raw.includes("in") || raw.includes("offset") || raw.includes("print")) {
    return {
      bg: "bg-purple-50 border-purple-300 text-purple-950 hover:bg-purple-100 shadow-2xs",
      indicator: "bg-purple-600",
      badge: "bg-purple-100 text-purple-800 border-purple-200",
    };
  }
  if (raw.includes("bế") || raw.includes("die") || raw.includes("dập")) {
    return {
      bg: "bg-amber-50 border-amber-300 text-amber-950 hover:bg-amber-100 shadow-2xs",
      indicator: "bg-amber-500",
      badge: "bg-amber-100 text-amber-800 border-amber-200",
    };
  }
  if (raw.includes("cán") || raw.includes("màng") || raw.includes("laminat")) {
    return {
      bg: "bg-teal-50 border-teal-300 text-teal-950 hover:bg-teal-100 shadow-2xs",
      indicator: "bg-teal-600",
      badge: "bg-teal-100 text-teal-800 border-teal-200",
    };
  }
  if (raw.includes("cắt") || raw.includes("xén") || raw.includes("cut")) {
    return {
      bg: "bg-sky-50 border-sky-300 text-sky-950 hover:bg-sky-100 shadow-2xs",
      indicator: "bg-sky-600",
      badge: "bg-sky-100 text-sky-800 border-sky-200",
    };
  }
  if (raw.includes("dán") || raw.includes("hộp") || raw.includes("gluing")) {
    return {
      bg: "bg-indigo-50 border-indigo-300 text-indigo-950 hover:bg-indigo-100 shadow-2xs",
      indicator: "bg-indigo-600",
      badge: "bg-indigo-100 text-indigo-800 border-indigo-200",
    };
  }
  if (raw.includes("đóng gói") || raw.includes("kcs") || raw.includes("packing")) {
    return {
      bg: "bg-emerald-50 border-emerald-300 text-emerald-950 hover:bg-emerald-100 shadow-2xs",
      indicator: "bg-emerald-600",
      badge: "bg-emerald-100 text-emerald-800 border-emerald-200",
    };
  }

  return {
    bg: "bg-slate-50 border-slate-300 text-slate-900 hover:bg-slate-100 shadow-2xs",
    indicator: "bg-slate-500",
    badge: "bg-slate-100 text-slate-700 border-slate-200",
  };
}

export function ProcessGanttView({
  items = [],
  isLoading = false,
  dateParams,
  highlightOrder,
  onNavigateToOrder,
  onNavigateToSalesOrder,
  onOpenMachineModal,
  onOpenLogForm,
  onRefetch,
}: ProcessGanttViewProps) {
  // Query all active machines from system
  const { data: machinesData, isLoading: isMachinesLoading } = useQuery(
    trpc.machines.list.queryOptions({ limit: 100 })
  );
  const machinesList: any[] =
    (machinesData as any)?.items ||
    (machinesData as any)?.data ||
    (Array.isArray(machinesData) ? machinesData : []) ||
    [];

  // "Xếp lại theo hạn" theo từng máy (xem trước rồi mới lưu)
  const [resequenceMachine, setResequenceMachine] = useState<{ id: string; name?: string; code?: string } | null>(null);

  // Zoom Mode: DAY (7-14 ngày) vs HOUR (48 giờ chi tiết)
  const [zoomMode, setZoomMode] = useState<"DAY" | "HOUR">("DAY");
  const [timelineOffsetDays, setTimelineOffsetDays] = useState(0);

  // Time Window Calculations
  const { startDate, endDate, days, hours, totalDurationMs } = useMemo(() => {
    let baseStart = dateParams.from
      ? startOfDay(parseISO(dateParams.from))
      : undefined;

    // Nếu không truyền dateParams.from:
    if (!baseStart) {
      let earliestStepDate: Date | null = null;
      if (highlightOrder && items.length > 0) {
        const orderSteps = items.filter(
          (s) =>
            s.productionOrderId === highlightOrder ||
            s.poCode === highlightOrder ||
            s.orderId === highlightOrder
        );
        if (orderSteps.length > 0) {
          const stepDates = orderSteps
            .map((s) => (s.plannedStartDate ? new Date(s.plannedStartDate) : null))
            .filter((d): d is Date => d !== null && !isNaN(d.getTime()));
          if (stepDates.length > 0) {
            earliestStepDate = new Date(Math.min(...stepDates.map((d) => d.getTime())));
          }
        }
      }

      if (earliestStepDate) {
        baseStart = addDays(startOfDay(earliestStepDate), -1);
      } else {
        // Mặc định hiển thị từ 7 ngày trước để bao quát cả các công đoạn đang gối đầu/chạy từ đầu tuần
        baseStart = addDays(startOfDay(new Date()), -7);
      }
    }

    if (timelineOffsetDays !== 0) {
      baseStart = addDays(baseStart, timelineOffsetDays);
    }

    if (zoomMode === "HOUR") {
      const start = baseStart;
      const end = addHours(start, 48); // 48 giờ chi tiết
      const hoursArr = Array.from({ length: 48 }).map((_, i) => addHours(start, i));
      return {
        startDate: start,
        endDate: end,
        days: [start, addDays(start, 1)],
        hours: hoursArr,
        totalDurationMs: end.getTime() - start.getTime(),
      };
    }

    // DAY View
    const start = baseStart;
    const end = dateParams.to
      ? startOfDay(parseISO(dateParams.to))
      : startOfDay(addDays(start, 21));

    const diff = Math.max(7, differenceInDays(end, start) + 1);
    const clampedDiff = Math.min(30, diff); // Tối đa 30 ngày trên 1 view
    const actualEnd = addDays(start, clampedDiff);
    const daysArr = Array.from({ length: clampedDiff }).map((_, i) => addDays(start, i));

    return {
      startDate: start,
      endDate: actualEnd,
      days: daysArr,
      hours: [],
      totalDurationMs: actualEnd.getTime() - start.getTime(),
    };
  }, [dateParams.from, dateParams.to, timelineOffsetDays, zoomMode, highlightOrder, items]);

  // Current time position indicator
  const now = new Date();
  const nowMs = now.getTime();
  const isNowInView = nowMs >= startDate.getTime() && nowMs <= endDate.getTime();
  const nowPositionPercent = isNowInView
    ? ((nowMs - startDate.getTime()) / totalDurationMs) * 100
    : null;

  // Collision Detection on Machine Schedules
  const { collisions, collisionStepIds, stepsByMachineId } = useMemo(() => {
    const byMachine: Record<string, any[]> = {};
    const collisionList: CollisionInfo[] = [];
    const collisionIds = new Set<string>();

    // Phân loại các step vào từng machine
    items.forEach((step) => {
      const mId = step.machineId || "UNASSIGNED";
      if (!byMachine[mId]) byMachine[mId] = [];
      byMachine[mId].push(step);
    });

    // Quét xung đột theo từng máy
    Object.entries(byMachine).forEach(([mId, steps]) => {
      if (mId === "UNASSIGNED") return;
      for (let i = 0; i < steps.length; i++) {
        const stepA = steps[i];
        if (!stepA.plannedStartDate || !stepA.plannedEndDate) continue;
        const startA = new Date(stepA.plannedStartDate).getTime();
        const endA = new Date(stepA.plannedEndDate).getTime();
        if (isNaN(startA) || isNaN(endA) || endA <= startA) continue;

        for (let j = i + 1; j < steps.length; j++) {
          const stepB = steps[j];
          if (!stepB.plannedStartDate || !stepB.plannedEndDate) continue;
          const startB = new Date(stepB.plannedStartDate).getTime();
          const endB = new Date(stepB.plannedEndDate).getTime();
          if (isNaN(startB) || isNaN(endB) || endB <= startB) continue;

          // Kiểm tra giao nhau: startA < endB && endA > startB
          if (startA < endB && endA > startB) {
            const overlapStart = Math.max(startA, startB);
            const overlapEnd = Math.min(endA, endB);
            collisionList.push({
              machineId: mId,
              stepA,
              stepB,
              overlapStart,
              overlapEnd,
            });
            collisionIds.add(stepA.id);
            collisionIds.add(stepB.id);
          }
        }
      }
    });

    return {
      collisions: collisionList,
      collisionStepIds: collisionIds,
      stepsByMachineId: byMachine,
    };
  }, [items]);

  // Combine machine list: All active machines + "Chưa gán máy"
  const displayMachines = useMemo(() => {
    const list = [...machinesList];
    // Sắp xếp máy: ưu tiên máy có công đoạn đang xếp lịch
    list.sort((a, b) => {
      const countA = (
        stepsByMachineId[a.id] ||
        stepsByMachineId[a.machineId] ||
        []
      ).length;
      const countB = (
        stepsByMachineId[b.id] ||
        stepsByMachineId[b.machineId] ||
        []
      ).length;
      return countB - countA;
    });

    // Thêm lane "Chưa gán máy" nếu có step chưa chọn máy
    if (stepsByMachineId["UNASSIGNED"] && stepsByMachineId["UNASSIGNED"].length > 0) {
      list.push({
        id: "UNASSIGNED",
        name: "⚠️ Chờ xếp máy (Chưa gán)",
        code: "UNASSIGNED",
        type: "Chờ điều phối",
      });
    }

    return list;
  }, [machinesList, stepsByMachineId]);

  // Helper tính vị trí Task Block trên Gantt
  const calculateTaskStyle = (step: any) => {
    if (!step.plannedStartDate || !step.plannedEndDate) {
      return { display: "none" };
    }

    const start = new Date(step.plannedStartDate).getTime();
    const end = new Date(step.plannedEndDate).getTime();

    if (isNaN(start) || isNaN(end) || end <= start) {
      return { display: "none" };
    }

    // Nằm ngoài hoàn toàn cửa sổ hiển thị
    if (end < startDate.getTime() || start > endDate.getTime()) {
      return { display: "none" };
    }

    const leftMs = Math.max(0, start - startDate.getTime());
    const visibleEndMs = Math.min(endDate.getTime(), end);
    const durationMs = Math.max(1800000, visibleEndMs - Math.max(startDate.getTime(), start)); // Min 30 phút

    const leftPercent = (leftMs / totalDurationMs) * 100;
    const widthPercent = Math.max(1.5, (durationMs / totalDurationMs) * 100);

    return {
      left: `${leftPercent}%`,
      width: `${Math.min(100 - leftPercent, widthPercent)}%`,
    };
  };

  // Helper tính vị trí dải Collision Overlay
  const calculateCollisionOverlayStyle = (col: CollisionInfo) => {
    if (col.overlapEnd < startDate.getTime() || col.overlapStart > endDate.getTime()) {
      return { display: "none" };
    }
    const leftMs = Math.max(0, col.overlapStart - startDate.getTime());
    const visibleEndMs = Math.min(endDate.getTime(), col.overlapEnd);
    const durationMs = Math.max(900000, visibleEndMs - Math.max(startDate.getTime(), col.overlapStart));

    const leftPercent = (leftMs / totalDurationMs) * 100;
    const widthPercent = Math.max(1, (durationMs / totalDurationMs) * 100);

    return {
      left: `${leftPercent}%`,
      width: `${Math.min(100 - leftPercent, widthPercent)}%`,
    };
  };

  const totalAssignedSteps = items.filter((s) => s.machineId).length;
  // LSX trễ hạn theo lập lịch năng lực máy (production_orders.schedule_late_days)
  const lateOrders = useMemo(() => {
    const map = new Map<string, number>();
    items.forEach((s) => {
      if (Number(s.scheduleLateDays) > 0) map.set(s.poCode || s.productionOrderId, Number(s.scheduleLateDays));
    });
    return map;
  }, [items]);

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex flex-col bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs min-h-[580px]">
        {/* Top Control Bar của Gantt Chart */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 font-bold text-slate-800 text-sm">
              <Calendar className="w-4 h-4 text-teal-600" />
              <span>Gantt Điều Độ Lịch Máy APS</span>
            </div>

            <div className="h-4 w-px bg-slate-200" />

            {/* Quick Metrics */}
            <div className="flex items-center gap-3 text-xs">
              <div className="flex items-center gap-1 text-slate-600">
                <Cpu className="w-3.5 h-3.5 text-slate-500" />
                <span>Máy hoạt động:</span>
                <span className="font-bold font-mono text-slate-800">
                  {machinesList.length}
                </span>
              </div>
              <div className="flex items-center gap-1 text-slate-600">
                <Layers className="w-3.5 h-3.5 text-teal-600" />
                <span>Đã xếp lịch:</span>
                <span className="font-bold font-mono text-teal-700">
                  {totalAssignedSteps}/{items.length}
                </span>
              </div>
              {lateOrders.size > 0 && (
                <div
                  className="flex items-center gap-1 text-amber-800 bg-amber-50 border border-amber-300 px-2 py-0.5 rounded-full font-bold"
                  data-testid="gantt-late-orders"
                  title={[...lateOrders].map(([po, d]) => `${po}: trễ ${d} ngày`).join("\n")}
                >
                  <Clock className="w-3.5 h-3.5 text-amber-600" />
                  <span>{lateOrders.size} LSX trễ hạn</span>
                </div>
              )}
              {collisions.length > 0 && (
                <div className="flex items-center gap-1 text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded-full font-bold animate-pulse">
                  <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />
                  <span>{collisions.length} Xung đột trùng máy</span>
                </div>
              )}
            </div>
          </div>

          {/* Right Toolbar: Zoom Mode & Navigation */}
          <div className="flex items-center gap-2">
            {/* Legend Popover / Mini Legend */}
            <div className="hidden lg:flex items-center gap-2 text-[11px] text-slate-500 mr-2">
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-xs bg-purple-500" /> In
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-xs bg-amber-500" /> Bế
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-xs bg-teal-500" /> Cán
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-xs bg-indigo-500" /> Dán
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-xs bg-rose-500 border border-rose-600" /> Trùng lịch
              </span>
              <span className="flex items-center gap-1">
                <Lock className="w-2.5 h-2.5 text-slate-600" /> Đã khóa lịch
              </span>
            </div>

            {/* Zoom Toggle */}
            <div className="inline-flex items-center bg-white border border-slate-200 rounded-lg p-0.5 shadow-2xs">
              <button
                type="button"
                onClick={() => setZoomMode("DAY")}
                className={cn(
                  "px-2.5 py-1 text-xs font-semibold rounded-md transition-all cursor-pointer",
                  zoomMode === "DAY"
                    ? "bg-teal-50 text-teal-700 font-bold"
                    : "text-slate-600 hover:text-slate-900"
                )}
              >
                Theo Ngày
              </button>
              <button
                type="button"
                onClick={() => setZoomMode("HOUR")}
                className={cn(
                  "px-2.5 py-1 text-xs font-semibold rounded-md transition-all cursor-pointer",
                  zoomMode === "HOUR"
                    ? "bg-teal-50 text-teal-700 font-bold"
                    : "text-slate-600 hover:text-slate-900"
                )}
              >
                Chi Tiết Giờ
              </button>
            </div>

            {/* Time Navigation */}
            <div className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-0.5 shadow-2xs">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-slate-600 hover:bg-slate-100"
                onClick={() => setTimelineOffsetDays((prev) => prev - (zoomMode === "HOUR" ? 2 : 7))}
                title="Lùi thời gian"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs font-bold text-slate-700 hover:bg-slate-100"
                onClick={() => setTimelineOffsetDays(0)}
              >
                Hiện tại
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-slate-600 hover:bg-slate-100"
                onClick={() => setTimelineOffsetDays((prev) => prev + (zoomMode === "HOUR" ? 2 : 7))}
                title="Tiến thời gian"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Gantt Scroll Container */}
        <div className="flex-1 overflow-auto relative">
          <div className="flex min-w-max">
            {/* Cột Máy bên trái (Solid Sticky Column chống xuyên thấu) */}
            <div
              className="w-64 min-w-[256px] border-r border-slate-200 bg-white sticky left-0 z-30 shadow-[2px_0_6px_-2px_rgba(0,0,0,0.08)]"
              style={{ backgroundColor: "#ffffff", isolation: "isolate" }}
            >
              {/* Header Cột Máy */}
              <div
                className="h-12 border-b border-slate-200 px-3.5 flex items-center font-bold text-xs text-slate-700 uppercase tracking-wider sticky top-0 z-40 bg-slate-100"
                style={{ backgroundColor: "#f1f5f9", isolation: "isolate" }}
              >
                Máy / Dây Chuyền Xưởng
              </div>

              {/* Danh sách các Hàng Máy */}
              {displayMachines.map((machine) => {
                const assignedSteps =
                  stepsByMachineId[machine.id] ||
                  stepsByMachineId[machine.machineId] ||
                  (machine.id === "UNASSIGNED" ? stepsByMachineId["UNASSIGNED"] : []) ||
                  [];
                const hasCollision = collisions.some(
                  (c) => c.machineId === machine.id || c.machineId === machine.machineId
                );

                return (
                  <div
                    key={machine.id}
                    className={cn(
                      "h-24 min-h-[6rem] border-b border-slate-200 px-3 py-2 flex flex-col justify-between transition-colors",
                      hasCollision ? "bg-rose-50/40" : "bg-white hover:bg-slate-50/60"
                    )}
                  >
                    <div>
                      <div className="flex items-center justify-between gap-1">
                        <span className="font-bold text-xs text-slate-900 truncate" title={machine.name}>
                          {machine.name}
                        </span>
                        {hasCollision && (
                          <span className="shrink-0 inline-flex items-center px-1.5 py-0.2 rounded text-[10px] font-bold bg-rose-100 text-rose-700 border border-rose-200">
                            Trùng lịch
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono mt-0.5 truncate">
                        {machine.code || machine.machineId || "MÃ-MÁY"} • {machine.type || "Tiêu chuẩn"}
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1 border-t border-slate-100">
                      <span>Đang xếp:</span>
                      <span className="font-mono font-bold text-teal-700 bg-teal-50 px-1.5 py-0.2 rounded">
                        {assignedSteps.length} công đoạn
                      </span>
                    </div>
                    {assignedSteps.length > 1 && machine.id !== "UNASSIGNED" && (
                      <button
                        type="button"
                        className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-700 hover:underline self-start"
                        onClick={() => setResequenceMachine({ id: machine.id, name: machine.name, code: machine.code })}
                        title="Xếp các công đoạn chưa chạy của máy theo hạn giao (xem trước rồi mới lưu)"
                        data-testid="resequence-open"
                      >
                        <CalendarClock className="w-3 h-3" /> Xếp theo hạn
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Vùng Lưới Timeline Gantt */}
            <div
              className="flex-1 relative flex flex-col"
              style={{
                width:
                  zoomMode === "HOUR"
                    ? `calc(60px * ${hours.length})`
                    : `calc(130px * ${days.length})`,
              }}
            >
              {/* Header Trục Thời Gian (Sticky Top) */}
              <div
                className="h-12 border-b border-slate-200 sticky top-0 z-20 flex bg-slate-100 select-none shadow-2xs"
                style={{ backgroundColor: "#f1f5f9" }}
              >
                {zoomMode === "HOUR"
                  ? hours.map((hour) => {
                      const isHourToday = isToday(hour);
                      return (
                        <div
                          key={hour.toISOString()}
                          className={cn(
                            "flex-1 min-w-[60px] border-r border-slate-200 flex flex-col items-center justify-center text-center py-1",
                            isHourToday && "bg-teal-50/50"
                          )}
                        >
                          <span className="text-[10px] text-slate-500">
                            {format(hour, "dd/MM")}
                          </span>
                          <span className="text-xs font-mono font-bold text-slate-700">
                            {format(hour, "HH:mm")}
                          </span>
                        </div>
                      );
                    })
                  : days.map((day) => {
                      const isCurrentDay = isToday(day);
                      return (
                        <div
                          key={day.toISOString()}
                          className={cn(
                            "flex-1 min-w-[130px] border-r border-slate-200 flex flex-col items-center justify-center text-center py-1 transition-colors",
                            isCurrentDay && "bg-teal-100/60 font-bold border-teal-300"
                          )}
                        >
                          <span
                            className={cn(
                              "text-[10px] uppercase font-semibold",
                              isCurrentDay ? "text-teal-800" : "text-slate-500"
                            )}
                          >
                            {format(day, "EEEE", { locale: vi })}
                          </span>
                          <span
                            className={cn(
                              "text-xs font-bold font-mono",
                              isCurrentDay ? "text-teal-900" : "text-slate-800"
                            )}
                          >
                            {format(day, "dd/MM/yyyy")}
                          </span>
                        </div>
                      );
                    })}
              </div>

              {/* Vạch Thời Gian Hiện Tại (Current Time Line) */}
              {nowPositionPercent !== null && (
                <div
                  className="absolute top-0 bottom-0 pointer-events-none z-20 border-l-2 border-rose-500 shadow-sm"
                  style={{ left: `${nowPositionPercent}%` }}
                >
                  <div className="sticky top-12 -ml-9 bg-rose-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded shadow-md whitespace-nowrap">
                    ● {format(now, "HH:mm dd/MM")}
                  </div>
                </div>
              )}

              {/* Lưới Nền (Grid Lines) */}
              <div className="absolute inset-0 flex pointer-events-none">
                {(zoomMode === "HOUR" ? hours : days).map((d) => (
                  <div
                    key={`grid-${d.toISOString()}`}
                    className={cn(
                      "flex-1 border-r border-slate-100 h-full",
                      isToday(d) && "bg-teal-50/15"
                    )}
                    style={{
                      minWidth: zoomMode === "HOUR" ? "60px" : "130px",
                    }}
                  />
                ))}
              </div>

              {/* Hàng Timeline Từng Máy */}
              {displayMachines.map((machine) => {
                const assignedSteps =
                  stepsByMachineId[machine.id] ||
                  stepsByMachineId[machine.machineId] ||
                  (machine.id === "UNASSIGNED" ? stepsByMachineId["UNASSIGNED"] : []) ||
                  [];
                const machineCollisions = collisions.filter(
                  (c) => c.machineId === machine.id || c.machineId === machine.machineId
                );

                return (
                  <div
                    key={`timeline-lane-${machine.id}`}
                    className="h-24 min-h-[6rem] border-b border-slate-200 relative group transition-colors hover:bg-slate-50/40"
                  >
                    {/* Dải Cảnh Báo Trùng Máy (Machine Collision Overlays) */}
                    {machineCollisions.map((col, idx) => {
                      const colStyle = calculateCollisionOverlayStyle(col);
                      if (colStyle.display === "none") return null;

                      return (
                        <Tooltip key={`col-${machine.id}-${idx}`}>
                          <TooltipTrigger asChild>
                            <div
                              className="absolute top-1 bottom-1 rounded border border-rose-500/80 cursor-help z-10 animate-pulse"
                              style={{
                                ...colStyle,
                                background:
                                  "repeating-linear-gradient(45deg, rgba(244, 63, 94, 0.2), rgba(244, 63, 94, 0.2) 8px, rgba(244, 63, 94, 0.35) 8px, rgba(244, 63, 94, 0.35) 16px)",
                              }}
                            />
                          </TooltipTrigger>
                          <TooltipContent side="top" className="bg-slate-900 text-white max-w-xs p-2.5 text-xs">
                            <div className="font-bold text-rose-300 flex items-center gap-1 mb-1">
                              <AlertTriangle className="w-3.5 h-3.5 text-rose-400" />
                              Xung đột trùng lịch máy!
                            </div>
                            <div className="text-[11px] space-y-1">
                              <div>
                                • <strong>{col.stepA.poCode}</strong>: {col.stepA.name} ({format(new Date(col.stepA.plannedStartDate), "dd/MM HH:mm")} - {format(new Date(col.stepA.plannedEndDate), "dd/MM HH:mm")})
                              </div>
                              <div>
                                • <strong>{col.stepB.poCode}</strong>: {col.stepB.name} ({format(new Date(col.stepB.plannedStartDate), "dd/MM HH:mm")} - {format(new Date(col.stepB.plannedEndDate), "dd/MM HH:mm")})
                              </div>
                              <div className="text-amber-300 text-[10px] mt-1 pt-1 border-t border-slate-700">
                                💡 Nhấp vào công đoạn để mở modal dời lịch hoặc đổi sang máy khác.
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}

                    {/* Render Các Task Block Công Đoạn */}
                    {assignedSteps.map((step) => {
                      const baseStyle = calculateTaskStyle(step);
                      if (baseStyle.display === "none") return null;
                      // Khối ngắn được vẽ tối thiểu ~1,5% khung thời gian nên có thể đè lên khối kế tiếp cùng máy:
                      // khối bắt đầu sau nằm trên, khối đang làm nổi bật luôn trên cùng (vẫn dưới cột máy cố định z-30) → khối nào cũng bấm được
                      const startRank = assignedSteps.filter(
                        (o: any) => new Date(o.plannedStartDate || 0).getTime() < new Date(step.plannedStartDate || 0).getTime()
                      ).length;

                      const isCollision = collisionStepIds.has(step.id);
                      const isLocked = Boolean(step.scheduleLocked);
                      const lateDays = Number(step.scheduleLateDays) || 0;
                      const suggestions: any[] = Array.isArray(step.scheduleSuggestions) ? step.scheduleSuggestions : [];
                      const isHighlighted =
                        Boolean(highlightOrder) &&
                        (step.poCode === highlightOrder ||
                          step.productionOrderId === highlightOrder ||
                          step.id === highlightOrder);
                      const colors = getStepColorClasses(step.name || step.processName, isCollision);

                      const prevStepInPo = items.find(
                        (s) =>
                          s.productionOrderId === step.productionOrderId &&
                          Number(s.stepOrder || 0) === Number(step.stepOrder || 0) - 1
                      );
                      const stepNameStr = String(step.name || step.processName || "");
                      const prevNameStr = prevStepInPo
                        ? String(prevStepInPo.name || prevStepInPo.processName || "")
                        : stepNameStr.toLowerCase().includes("cán")
                        ? "In Offset"
                        : "";
                      const curingEval = evaluateInkCuringDelay(
                        prevNameStr,
                        stepNameStr,
                        Boolean(prevNameStr.toUpperCase().includes("UV") || stepNameStr.toUpperCase().includes("UV"))
                      );

                      return (
                        <Tooltip key={step.id}>
                          <TooltipTrigger asChild>
                            <div
                              data-testid="gantt-task"
                              data-step-id={step.id}
                              data-po-code={step.poCode || ""}
                              data-locked={isLocked ? "true" : "false"}
                              data-late-days={lateDays}
                              className={cn(
                                "absolute top-2.5 bottom-2.5 rounded-lg border shadow-xs p-1.5 overflow-hidden cursor-pointer transition-all flex flex-col justify-between z-15 select-none",
                                colors.bg,
                                isCollision && "border-rose-500 ring-1 ring-rose-400",
                                isHighlighted &&
                                  "ring-2 ring-indigo-600 ring-offset-2 shadow-md animate-pulse font-bold"
                              )}
                              style={{ ...baseStyle, zIndex: isHighlighted ? 29 : 15 + Math.min(startRank, 12) }}
                              onClick={() => onOpenMachineModal?.(step)}
                            >
                              {/* Dòng 1: Mã LSX + Trạng thái */}
                              <div className="flex items-center justify-between gap-1 leading-none">
                                <span className="font-bold text-[11px] truncate flex items-center gap-1 font-mono">
                                  <span className={cn("w-1.5 h-1.5 rounded-full shrink-0", colors.indicator)} />
                                  {step.poCode || "LSX"}
                                </span>
                                <span className="flex items-center gap-0.5 shrink-0">
                                  {lateDays > 0 && (
                                    <span
                                      data-testid="gantt-task-late"
                                      className="px-1 rounded bg-amber-500 text-white text-[9px] font-bold leading-tight"
                                      title={`LSX trễ ${lateDays} ngày so với hạn giao`}
                                    >
                                      Trễ {lateDays}n
                                    </span>
                                  )}
                                  {isLocked && (
                                    <Lock
                                      data-testid="gantt-task-lock"
                                      className="w-3 h-3 text-slate-700"
                                      aria-label="Đã khóa lịch"
                                    />
                                  )}
                                  {isCollision && (
                                    <AlertTriangle className="w-3 h-3 text-rose-600 animate-bounce" />
                                  )}
                                </span>
                              </div>

                              {/* Dòng 2: Tên công đoạn + SL */}
                              <div className="text-[10px] font-semibold truncate leading-tight mt-0.5">
                                {step.name || step.processName}
                              </div>

                              {/* Dòng 3: Badges tiền trạm & Gối đầu APS */}
                              <div className="flex items-center gap-1 text-[9px] mt-0.5 leading-none">
                                <span className="font-mono opacity-80 truncate">
                                  SL: {formatNumber(step.standardQuantity || 0)}
                                </span>

                                {curingEval.curingDelayHours > 0 && (
                                  <span
                                    data-testid="gantt-curing-delay-badge"
                                    className="px-1 py-0.2 rounded bg-amber-100 text-amber-900 font-bold border border-amber-300 shrink-0"
                                    title={curingEval.reason ?? undefined}
                                  >
                                    ⏳Chờ khô {curingEval.curingDelayHours}h
                                  </span>
                                )}

                                {step.overlapPercent !== null && step.overlapPercent > 0 && (
                                  <span
                                    className="px-1 py-0.2 rounded bg-purple-100 text-purple-800 font-bold border border-purple-200 shrink-0"
                                    title={describeOverlap(step.overlapPercent)}
                                  >
                                    ⚡{step.overlapPercent}%
                                  </span>
                                )}

                                {/* Pre-flight icons */}
                                {step.materialStatus === "READY" && (
                                  <span title="Vật tư đã sẵn sàng" className="text-emerald-600">📦</span>
                                )}
                                {step.toolingStatus === "READY" && (
                                  <span title="Khuôn/Kẽm đã sẵn sàng" className="text-emerald-600">✂️</span>
                                )}
                              </div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="bg-slate-900 text-white p-3 max-w-sm text-xs shadow-xl">
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between gap-2 border-b border-slate-700 pb-1">
                                <span className="font-bold text-teal-300 font-mono text-sm">
                                  {step.poCode}
                                </span>
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-800 text-slate-300">
                                  {step.status}
                                </span>
                              </div>

                              <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[11px] text-slate-300">
                                <div>
                                  <span className="text-slate-400">Công đoạn:</span>{" "}
                                  <span className="font-semibold text-white">{step.name}</span>
                                </div>
                                <div>
                                  <span className="text-slate-400">Máy:</span>{" "}
                                  <span className="font-semibold text-white">{step.machineName}</span>
                                </div>
                                <div>
                                  <span className="text-slate-400">Kế hoạch:</span>{" "}
                                  <span className="font-mono text-white font-bold">
                                    {formatNumber(step.standardQuantity || 0)} {step.productionUnit || "tờ"}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-slate-400">Đã đạt:</span>{" "}
                                  <span className="font-mono text-emerald-400 font-bold">
                                    {formatNumber(step.actualQtyOk || 0)}
                                  </span>
                                </div>
                                <div className="col-span-2">
                                  <span className="text-slate-400">Bắt đầu:</span>{" "}
                                  <span className="font-mono text-white">
                                    {step.plannedStartDate ? format(new Date(step.plannedStartDate), "dd/MM/yyyy HH:mm") : "—"}
                                  </span>
                                </div>
                                <div className="col-span-2">
                                  <span className="text-slate-400">Hạn xong:</span>{" "}
                                  <span className="font-mono text-white">
                                    {step.plannedEndDate ? format(new Date(step.plannedEndDate), "dd/MM/yyyy HH:mm") : "—"}
                                  </span>
                                </div>
                                {step.customerName && (
                                  <div className="col-span-2 text-slate-400 truncate">
                                    Khách hàng: <span className="text-slate-200">{step.customerName}</span>
                                  </div>
                                )}
                              </div>

                              {curingEval.curingDelayHours > 0 && (
                                <div className="bg-amber-950/80 border border-amber-700/60 p-1.5 rounded text-amber-200 text-[10px] font-medium flex items-center gap-1">
                                  <Clock className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                                  <span>{curingEval.reason}</span>
                                </div>
                              )}

                              {isLocked && (
                                <div className="bg-slate-800 border border-slate-600 p-1.5 rounded text-slate-200 text-[10px] font-semibold flex items-center gap-1">
                                  <Lock className="w-3.5 h-3.5 shrink-0" />
                                  <span>Đã khóa lịch: lập lịch tự động không dời công đoạn này.</span>
                                </div>
                              )}

                              {(lateDays > 0 || step.scheduleNote) && (
                                <div
                                  className="bg-amber-950/80 border border-amber-700/60 p-1.5 rounded text-amber-200 text-[10px] font-medium space-y-0.5"
                                  data-testid="gantt-task-schedule-note"
                                >
                                  {lateDays > 0 && <div className="font-bold">LSX trễ {lateDays} ngày so với hạn giao.</div>}
                                  {step.scheduleNote && <div>{step.scheduleNote}</div>}
                                  {suggestions.length > 0 && (
                                    <div className="pt-0.5">
                                      Gợi ý máy cùng loại xong sớm hơn:
                                      {suggestions.map((sg) => (
                                        <div key={sg.machineId} className="font-mono">
                                          • {sg.machineName || sg.machineCode}: {formatVnDateTime(sg.start)} → {formatVnDateTime(sg.end)}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}

                              {isCollision && (
                                <div className="bg-rose-950/80 border border-rose-700/60 p-1.5 rounded text-rose-300 text-[10px] font-semibold flex items-center gap-1">
                                  <AlertTriangle className="w-3.5 h-3.5 text-rose-400 shrink-0" />
                                  <span>Cảnh báo: Trùng lịch với công đoạn khác trên máy này!</span>
                                </div>
                              )}

                              <div className="pt-1.5 border-t border-slate-700 flex items-center justify-between text-[10px]">
                                <span className="text-slate-400">Nhấp để dời lịch máy</span>
                                {onNavigateToOrder && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onNavigateToOrder(step.poCode);
                                    }}
                                    className="text-teal-400 hover:text-teal-300 font-semibold flex items-center gap-1 cursor-pointer"
                                  >
                                    Xem LSX <ExternalLink className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <ResequenceByDeadlineDialog
        machine={resequenceMachine}
        onClose={() => setResequenceMachine(null)}
        onApplied={() => onRefetch?.()}
      />
    </TooltipProvider>
  );
}
export default ProcessGanttView;
